/**
 * DemoChooserPage (`/demo`) — Patch 34.1.10.
 *
 * The route ``/demo`` no longer silently opens a single experience.
 * Visitors choose explicitly between the Ready-to-Work Workspace demo
 * (finished product) and the Brain Builder demo (how a new business
 * teaches Orkexra who it is).
 *
 * Security invariants (Patches 34.1.6 / 34.1.9): both demo paths keep
 * AI generation, exports, uploads, and internal metadata redacted for
 * non-admin visitors.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Brain, ChevronRight, Compass, Layers, Sparkles } from "lucide-react";
import { useEffect } from "react";

import { trackDemoEvent } from "@/shared/lib/demoAnalytics";

export default function DemoChooserPage(): JSX.Element {
  useEffect(() => {
    trackDemoEvent("contest_landing_viewed", "/demo");
  }, []);

  return (
    <div
      data-testid="demo-chooser-page"
      className="min-h-screen bg-[var(--md-ink-0,#0b0f14)] text-[var(--md-ivory-0,#e8e6df)]"
    >
      <div className="sticky top-0 z-30 flex items-center border-b border-[color:var(--md-line)] bg-[var(--md-ink-0)]/90 px-4 py-2 backdrop-blur-lg">
        <Link to="/welcome" data-testid="chooser-back" className="md-btn md-btn--ghost inline-flex text-xs">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Orkexra
        </Link>
      </div>

      <main className="mx-auto max-w-[1180px] px-6 py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="md-eyebrow md-eyebrow--amber">Choose how to explore Orkexra</div>
          <h1 className="mt-4 font-display italic tracking-tight text-4xl md:text-6xl text-[var(--md-ivory-0)]">
            Two doors.<br />
            <span style={{ color: "var(--md-amber)" }}>One product.</span>
          </h1>
          <p className="mt-6 text-[15px] leading-relaxed text-[var(--md-fog)]">
            See the finished system, or watch it learn a brand from scratch.
            Both paths are guest-safe — no sign-in, no credentials, no data
            leaving the demo.
          </p>
        </div>

        <div className="mx-auto mt-14 grid gap-6 lg:grid-cols-2">
          {/* Primary card — Ready-to-Work Workspace */}
          <Link
            to="/demo/workspace"
            data-testid="chooser-workspace-cta"
            onClick={() => trackDemoEvent("seeded_demo_opened", "/demo")}
            className="group relative overflow-hidden rounded-2xl border border-[color:var(--md-amber)]/40 bg-gradient-to-b from-[var(--md-amber)]/10 to-transparent p-8 transition hover:border-[color:var(--md-amber)]/70"
          >
            <div className="absolute right-4 top-4 md-eyebrow md-eyebrow--amber">Primary</div>
            <span
              aria-hidden
              className="grid h-12 w-12 place-items-center rounded-xl border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/15 text-[var(--md-amber)]"
            >
              <Layers className="h-5 w-5" />
            </span>
            <h2 className="mt-5 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
              Explore Ready-to-Work Demo
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--md-fog)]">
              Step inside a fully configured Orkexra workspace with campaigns,
              content, approvals, scheduling, and publishing already in motion.
            </p>
            <ul className="mt-5 grid gap-2 text-[13px] text-[var(--md-ivory-0)]">
              {[
                "Completed Business Brain & Brand Brain",
                "Active campaigns and generated assets",
                "Review queue · Approved Vault · Calendar",
                "Publishing history · Dashboard analytics",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--md-amber)]" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--md-amber)] group-hover:gap-2 transition-all">
              Open workspace demo <ChevronRight className="h-3.5 w-3.5" />
            </div>
          </Link>

          {/* Secondary card — Brain Builder */}
          <Link
            to="/demo/brain-builder"
            data-testid="chooser-brain-builder-cta"
            onClick={() => trackDemoEvent("brain_builder_demo_opened", "/demo")}
            className="group relative overflow-hidden rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-8 transition hover:border-[color:var(--md-fog)]/50"
          >
            <div className="absolute right-4 top-4 md-eyebrow">Secondary</div>
            <span
              aria-hidden
              className="grid h-12 w-12 place-items-center rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-2)] text-[var(--md-ivory-0)]"
            >
              <Brain className="h-5 w-5" />
            </span>
            <h2 className="mt-5 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
              See How It Works
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--md-fog)]">
              Start with a blank workspace and watch Orkexra learn the business
              before creating marketing.
            </p>
            <ul className="mt-5 grid gap-2 text-[13px] text-[var(--md-ivory-0)]">
              {[
                "Business identity & audience",
                "Offers & voice",
                "Visual identity & market context",
                "Marketing preferences",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--md-fog)]" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--md-ivory-0)] group-hover:gap-2 transition-all">
              Open Brain Builder <ChevronRight className="h-3.5 w-3.5" />
            </div>
          </Link>
        </div>

        <div className="mx-auto mt-16 flex max-w-2xl items-start gap-3 rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4 text-[12px] leading-relaxed text-[var(--md-fog)]">
          <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--md-amber)]" />
          <p>
            Both demo experiences run in a public-preview sandbox. Live AI
            generation, exports, uploads, and internal cost/provider metadata
            are hidden — the walkthrough shows the finished product surface,
            not the plumbing.
          </p>
        </div>

        <div className="mx-auto mt-8 flex max-w-2xl items-center justify-center gap-2 md-eyebrow">
          <Compass className="h-3 w-3" /> Two ways in · One system
        </div>
      </main>
    </div>
  );
}
